import type { WallRun } from '../../../data/types';
import type { Vec2 } from '../../../geo/types';
import type { WaterTest } from './moat';
import { citySide } from './wallArcade';

/** The unit layout direction from a wall to its moat, at the point of the wall nearest `p`. */
export type OutwardField = (p: Vec2) => Vec2;

/*
 * Two runs whose ends lie this close meet at a joint and turn through it as one wall: the gates,
 * towers and the Amstel's crossing between the layout's runs span up to 33 m.
 */
const JOIN_M = 40;

/*
 * The direction at a point is the mean of the wall's own directions over this many metres of wall
 * either side of it, so a wall turns through every bend and joint over twice that length, however
 * short the segments there, and keeps its own direction between bends.
 */
const TURN_M = 20;

interface Stretch {
  readonly length: number;
  readonly angle: number;
}

interface Run {
  readonly id: string;
  readonly path: readonly Vec2[];
  readonly stretches: readonly Stretch[];
}

function distinctPoints(path: readonly Vec2[]): Vec2[] {
  return path.filter((p, i) => i === 0 || p[0] !== path[i - 1]![0] || p[1] !== path[i - 1]![1]);
}

/* Each segment's length and the angle of its normal on the side away from the city (citySide),
   which holds the moat. */
function stretchesOf(path: readonly Vec2[], side: 1 | -1): Stretch[] {
  return path.slice(1).map((b, i) => {
    const dx = b[0] - path[i]![0];
    const dy = b[1] - path[i]![1];
    return { length: Math.hypot(dx, dy), angle: Math.atan2(-side * dx, side * dy) };
  });
}

function endOf(run: Run, atStart: boolean): Vec2 {
  return atStart ? run.path[0]! : run.path[run.path.length - 1]!;
}

/* The stretches of the run joined at this end, walking away from the joint, up to TURN_M. */
function beyond(run: Run, atStart: boolean, runs: readonly Run[]): Stretch[] {
  const own = endOf(run, atStart);
  let nearest = JOIN_M;
  let walk: readonly Stretch[] = [];
  for (const other of runs) {
    if (other === run) continue;
    for (const otherStart of [true, false]) {
      const end = endOf(other, otherStart);
      const d = Math.hypot(end[0] - own[0], end[1] - own[1]);
      if (d < nearest) {
        nearest = d;
        walk = otherStart ? other.stretches : [...other.stretches].reverse();
      }
    }
  }
  const taken: Stretch[] = [];
  for (let reach = 0, k = 0; k < walk.length && reach < TURN_M; k++) {
    taken.push(walk[k]!);
    reach += walk[k]!.length;
  }
  return taken;
}

/* Angles along the run, each within half a turn of the one before, with their arc-length spans. */
function unwrapped(
  stretches: readonly Stretch[],
  firstStart: number
): (Stretch & { start: number })[] {
  let start = firstStart;
  let previous = stretches[0]?.angle ?? 0;
  return stretches.map((stretch) => {
    const turn = Math.atan2(Math.sin(stretch.angle - previous), Math.cos(stretch.angle - previous));
    previous += turn;
    const span = { length: stretch.length, angle: previous, start };
    start += stretch.length;
    return span;
  });
}

/* The mean angle over [s - TURN_M, s + TURN_M], over the part of that window the spans cover. */
function meanAngle(spans: readonly (Stretch & { start: number })[], s: number): number {
  let sum = 0;
  let covered = 0;
  for (const span of spans) {
    const lo = Math.max(span.start, s - TURN_M);
    const hi = Math.min(span.start + span.length, s + TURN_M);
    if (hi <= lo) continue;
    sum += span.angle * (hi - lo);
    covered += hi - lo;
  }
  return sum / covered;
}

/* Arc length along the path at the point nearest p. */
function arcLengthAt(path: readonly Vec2[], p: Vec2): number {
  let best = Infinity;
  let arc = 0;
  let walked = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i]!;
    const b = path[i + 1]!;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const length = Math.hypot(dx, dy);
    const t = Math.max(
      0,
      Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (length * length))
    );
    const d = Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
    if (d < best) {
      best = d;
      arc = walked + t * length;
    }
    walked += length;
  }
  return arc;
}

function fieldOf(run: Run, runs: readonly Run[]): OutwardField {
  const before = beyond(run, true, runs).reverse();
  const after = beyond(run, false, runs);
  const leadIn = before.reduce((sum, stretch) => sum + stretch.length, 0);
  const spans = unwrapped([...before, ...run.stretches, ...after], -leadIn);
  return (p) => {
    const angle = meanAngle(spans, arcLengthAt(run.path, p));
    return [Math.cos(angle), Math.sin(angle)];
  };
}

/**
 * Each wall run's outward direction, continuous along the run, through its bends and across the
 * joints where runs meet, so a rule that reads it changes nowhere in a step (STYLE §8.2).
 */
export function wallOutwardFields(
  walls: readonly WallRun[],
  isWater: WaterTest
): ReadonlyMap<string, OutwardField> {
  const runs: Run[] = walls.map((wall) => {
    const path = distinctPoints(wall.path);
    return {
      id: wall.id,
      path,
      stretches: stretchesOf(path, citySide(path, wall.thickness, isWater)),
    };
  });
  return new Map(runs.map((run) => [run.id, fieldOf(run, runs)]));
}
